import React, { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useParams, useNavigate } from 'react-router-dom';
import { LogOut, FileText, Layers, TrendingUp, Building2, Globe2, Rocket,
  Compass, Briefcase, DollarSign, Map as MapIcon, Archive, LayoutDashboard,
} from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { clearToken, getContent, getDocuments, getKpis, isAuthed, me, trackDocument } from '../investors/api';

const SECTIONS = [
  { key: 'overview',            label: 'Overview',        icon: LayoutDashboard },
  { key: 'investment-case',     label: 'Investment case', icon: Compass },
  { key: 'traction',            label: 'Traction',        icon: TrendingUp },
  { key: 'products',            label: 'Products',        icon: Layers },
  { key: 'market',              label: 'Market',          icon: Building2 },
  { key: 'business-model',      label: 'Business model',  icon: Briefcase },
  { key: 'growth',              label: 'Growth',          icon: Rocket },
  { key: 'international',       label: 'International',   icon: Globe2 },
  { key: 'financials',          label: 'Financials',      icon: DollarSign },
  { key: 'roadmap',             label: 'Roadmap',         icon: MapIcon },
  { key: 'data-room',           label: 'Data room',       icon: Archive },
];

/* ------------------------------------------------------------------ */
/* Portal shell                                                       */
/* ------------------------------------------------------------------ */

export const V2InvestorPortal = () => {
  useSEO({
    title: 'Keystone / Videra Investor Overview',
    description: 'Confidential Keystone Strategic Consultants investor portal.',
    path: '/investors/portal',
    noindex: true,
  });
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    if (!isAuthed()) { nav('/investors/login'); return; }
    me().then(setProfile).catch((e) => { setError(e.message); if (e.message.includes('Session')) nav('/investors/login'); });
  }, [nav]);

  if (!isAuthed()) return <Navigate to="/investors/login" replace />;

  const handleSignOut = () => { clearToken(); nav('/investors/login'); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="v2-investor-portal">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">Keystone / Videra</p>
            <h1 className="text-2xl font-medium tracking-tight text-slate-900">Investor Portal</h1>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {profile && (
              <div className="text-right hidden sm:block">
                <p className="text-slate-900 font-medium">{profile.full_name}</p>
                <p className="text-xs text-slate-500">{profile.company}</p>
              </div>
            )}
            <button onClick={handleSignOut} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-red-600 px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors" data-testid="investor-signout">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <aside>
          <nav className="space-y-0.5" data-testid="investor-portal-nav">
            {SECTIONS.map((s) => (
              <NavLink
                key={s.key}
                to={`/investors/portal/${s.key === 'overview' ? '' : s.key}`}
                end={s.key === 'overview'}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-md text-sm ${isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`}
                data-testid={`investor-nav-${s.key}`}
              >
                <s.icon className="w-3.5 h-3.5" /> {s.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main>
          {error && <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
          <Outlet context={{ profile }} />
        </main>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Overview section                                                   */
/* ------------------------------------------------------------------ */

const KPI_LABELS = {
  live_deployments: 'Live Deployments',
  active_trials: 'Active Trials',
  paying_customers: 'Paying Customers',
  mrr: 'MRR',
  arr: 'ARR',
  qualified_pipeline: 'Qualified Pipeline',
  trial_conversion: 'Trial → Paid Conversion',
  countries: 'Countries / Markets',
};
const PLACEHOLDER = 'Data available to approved investors';

export const V2InvestorOverview = () => {
  const [kpis, setKpis] = useState(null);
  useEffect(() => { getKpis().then(setKpis).catch(() => setKpis({})); }, []);
  return (
    <div className="space-y-6" data-testid="investor-overview">
      <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-2">Welcome</p>
        <h2 className="text-2xl font-medium tracking-tight text-slate-900">Keystone / Videra Investor Overview</h2>
        <p className="mt-3 text-slate-600 leading-relaxed max-w-3xl">
          This area provides approved investors and strategic partners with information covering the Videra product ecosystem, commercial traction, growth strategy, financial performance and investment opportunity.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(KPI_LABELS).map(([k, label]) => {
          const value = kpis?.[k];
          return (
            <div key={k} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`investor-kpi-${k}`}>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{label}</p>
              <p className={`mt-2 text-lg font-medium ${value ? 'text-slate-900' : 'text-slate-400 italic text-sm'}`}>
                {value || PLACEHOLDER}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Section renderer — reads content from DB per section-key           */
/* ------------------------------------------------------------------ */

const DEFAULT_SECTION_TITLES = {
  'investment-case': 'The Keystone / Videra investment case',
  traction: 'From product development to operational use',
  products: 'One technology philosophy. Multiple market opportunities.',
  market: 'Market context',
  'business-model': 'Business model',
  growth: 'Growth strategy',
  international: 'International opportunity',
  financials: 'Financials',
  roadmap: 'Product roadmap',
};

export const V2InvestorSection = () => {
  const { section } = useParams();
  const [content, setContent] = useState({});
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    getContent().then(setContent).catch(() => setContent({}));
    if (section === 'data-room') getDocuments().then(setDocs).catch(() => setDocs([]));
  }, [section]);

  if (section === 'data-room') return <DataRoom docs={docs} />;

  const c = content[section];
  const title = c?.title || DEFAULT_SECTION_TITLES[section] || section;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8" data-testid={`investor-section-${section}`}>
      <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-2">{section.replace(/-/g, ' ')}</p>
      <h2 className="text-2xl font-medium tracking-tight text-slate-900">{title}</h2>
      {c?.body ? (
        <div className="mt-6 prose prose-slate max-w-none text-slate-700 whitespace-pre-line leading-relaxed">
          {c.body}
        </div>
      ) : (
        <div className="mt-8 border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500">
          Content for this section is being prepared.
          <br /> Detailed information available on request.
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Data Room                                                          */
/* ------------------------------------------------------------------ */

const DataRoom = ({ docs }) => {
  const byCategory = docs.reduce((acc, d) => {
    (acc[d.category] = acc[d.category] || []).push(d);
    return acc;
  }, {});
  const handleClick = (id) => { trackDocument(id).catch(() => {}); };
  return (
    <div data-testid="investor-data-room">
      <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 mb-6">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-2">Data room</p>
        <h2 className="text-2xl font-medium tracking-tight text-slate-900">Confidential documents</h2>
        <p className="mt-3 text-sm text-slate-600 max-w-3xl">
          Please treat all contents as confidential. Document access is logged.
        </p>
      </div>
      {Object.keys(byCategory).length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-sm text-slate-500">
          No documents yet. Available on request.
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(byCategory).map(([cat, items]) => (
            <div key={cat} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">{cat}</p>
              </div>
              <ul className="divide-y divide-slate-100">
                {items.map((d) => (
                  <li key={d.id}>
                    <a href={d.url} target="_blank" rel="noopener noreferrer" onClick={() => handleClick(d.id)} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50" data-testid={`investor-doc-${d.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{d.title}</p>
                        {d.description && <p className="text-xs text-slate-500 mt-0.5 truncate">{d.description}</p>}
                      </div>
                      <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
